import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import { Visualisation } from "./visualisation";
import { THRESHOLDS } from "../thresholds";
import { Colors, Colormaps } from "../colors";
import { ColorBufferMaterial } from "../materials";

export class ScanValidation extends Visualisation {
  constructor(element, data) {
    super(element);

    this.scanGauges = new THREE.Group();
    this.narrowScanBodies = new THREE.Group();
    this.colorInterplator = 0;

    const { fixedScan, scanAnalysis } = data;

    const scanData = {
      positions: new Float32Array(fixedScan.mesh.vertexPositions),
      normals: new Float32Array(fixedScan.mesh.vertexNormals),
      indices: new Uint32Array(fixedScan.mesh.indices),
    };

    for (let i = 0; i < fixedScan.scanGauges.length; i++) {
      this.scanGauges.add(
        new ScanGauge(scanData, {
          faceIndices: fixedScan.scanGauges[i].faceIndices,
          distances: scanAnalysis.distances,
          score: scanAnalysis.scanGaugeComparisons[i].scoreRestorative,
        })
      );
    }

    for (let i = 0; i < fixedScan.narrowScanBodies?.length; i++) {
      this.narrowScanBodies.add(
        new NarrowScanBody(scanData, {
          faceIndices: fixedScan.narrowScanBodies[i].faceIndices,
          distances: scanAnalysis.distances,
          pass: scanAnalysis.narrowScanBodyComparisons[i].pass,
        })
      );
    }

    this.scanTissue = new ScanTissue(scanData, fixedScan.tissueFaceIndices);
    this.scene.add(this.scanGauges, this.narrowScanBodies, this.scanTissue);
  }

  dispose() {
    super.dispose();

    for (const scanGauge of this.scanGauges.children) {
      scanGauge.dispose();
    }

    for (const narrowScanBody of this.narrowScanBodies.children) {
      narrowScanBody.dispose();
    }

    this.scanGauges.clear();
    this.scanTissue.dispose();
  }

  showScoreColors() {
    this.colorInterplator = 1;
    new TWEEN.Tween(this)
      .to({ colorInterplator: 0 }, 1000)
      .easing(TWEEN.Easing.Quartic.Out)
      .onUpdate(({ colorInterplator }) => {
        for (const scanGauge of this.scanGauges.children) {
          scanGauge.colorBufferMaterial.uniforms.alpha.value = colorInterplator;
        }
        for (const narrowScanBody of this.narrowScanBodies.children) {
          narrowScanBody.colorBufferMaterial.uniforms.alpha.value =
            colorInterplator;
        }
      })
      .start();
  }

  showHeatmapColors() {
    this.colorInterplator = 0;
    new TWEEN.Tween(this)
      .to({ colorInterplator: 1 }, 1000)
      .easing(TWEEN.Easing.Quartic.Out)
      .onUpdate(({ colorInterplator }) => {
        for (const scanGauge of this.scanGauges.children) {
          scanGauge.colorBufferMaterial.uniforms.alpha.value = colorInterplator;
        }
        for (const narrowScanBody of this.narrowScanBodies.children) {
          narrowScanBody.colorBufferMaterial.uniforms.alpha.value =
            colorInterplator;
        }
      })
      .start();
  }

  getScanGaugesVisible() {
    return this.scanGauges.visible;
  }

  getNarrowScanBodiesVisible() {
    return this.narrowScanBodies.visible;
  }

  setScanGaugesVisible(visible) {
    this.scanGauges.visible = visible;
  }

  setNarrowScanBodiesVisible(visible) {
    this.narrowScanBodies.visible = visible;
  }

  getScanTissueVisible() {
    return this.scanTissue.visible;
  }

  setScanTissueVisible(visible) {
    this.scanTissue.visible = visible;
  }
}

class ScanGauge extends THREE.Object3D {
  constructor(scanData, gaugeData) {
    super();

    this.geometry = new THREE.BufferGeometry();
    this.colorBufferMaterial = new ColorBufferMaterial();
    this.uniformColor = new THREE.Color(Colors.primary);
    this.uniformColorMaterial = new THREE.MeshStandardMaterial({
      color: this.uniformColor,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.4,
    });

    const { faceIndices, distances, score } = gaugeData;

    const positions = new Float32Array(faceIndices.length * 3 * 3);
    const normals = new Float32Array(positions.length);
    const colors1 = new Float32Array(positions.length);
    const colors2 = new Float32Array(positions.length);

    const scoreColor = setScoreColor(new THREE.Color(), score).toArray();
    const heatmapColor = new THREE.Color();
    const heatmapColorArr = heatmapColor.toArray();

    let currentPositionIndex = 0;
    for (const faceIndex of faceIndices) {
      const index = faceIndex * 3;
      const vertexIndices = scanData.indices.slice(index, index + 3);
      for (const vertexIndex of vertexIndices) {
        const positionIndex = vertexIndex * 3;
        positions.set(
          scanData.positions.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        normals.set(
          scanData.normals.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        Colormaps.turbo(heatmapColor, distances[vertexIndex], 0.0, 0.1);
        colors1.set(scoreColor, currentPositionIndex);
        colors2.set(
          heatmapColor.toArray(heatmapColorArr),
          currentPositionIndex
        );
        currentPositionIndex += 3;
      }
    }

    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    this.geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    this.geometry.setAttribute("color1", new THREE.BufferAttribute(colors1, 3));
    this.geometry.setAttribute("color2", new THREE.BufferAttribute(colors2, 3));

    this.mesh = new THREE.Mesh(this.geometry, this.colorBufferMaterial);
    this.add(this.mesh);
  }

  setColorBufferMaterial() {
    this.mesh.material = this.colorBufferMaterial;
  }

  setUniformColorMaterial() {
    this.mesh.material = this.uniformColorMaterial;
  }

  setUniformColor(color) {
    this.uniformColorMaterial.color.setStyle(color);
  }

  dispose() {
    this.geometry.dispose();
    this.colorBufferMaterial.dispose();
    this.uniformColorMaterial.dispose();
  }
}

class NarrowScanBody extends THREE.Object3D {
  constructor(scanData, narrowScanBodyData) {
    super();

    this.geometry = new THREE.BufferGeometry();
    this.colorBufferMaterial = new ColorBufferMaterial();
    this.uniformColor = new THREE.Color(Colors.primary);
    this.uniformColorMaterial = new THREE.MeshStandardMaterial({
      color: this.uniformColor,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.4,
    });

    const { faceIndices, distances, pass } = narrowScanBodyData;

    const positions = new Float32Array(faceIndices.length * 3 * 3);
    const normals = new Float32Array(positions.length);
    const colors1 = new Float32Array(positions.length);
    const colors2 = new Float32Array(positions.length);

    const color = new THREE.Color();
    const validationColor = pass
      ? color.setStyle("#4CAF50").toArray()
      : color.setStyle("#FF5252").toArray();
    const heatmapColor = new THREE.Color();
    const heatmapColorArr = heatmapColor.toArray();

    let currentPositionIndex = 0;
    for (const faceIndex of faceIndices) {
      const index = faceIndex * 3;
      const vertexIndices = scanData.indices.slice(index, index + 3);
      for (const vertexIndex of vertexIndices) {
        const positionIndex = vertexIndex * 3;
        positions.set(
          scanData.positions.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        normals.set(
          scanData.normals.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        Colormaps.turbo(heatmapColor, distances[vertexIndex], 0.0, 0.1);
        colors1.set(validationColor, currentPositionIndex);
        colors2.set(
          heatmapColor.toArray(heatmapColorArr),
          currentPositionIndex
        );
        currentPositionIndex += 3;
      }
    }

    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    this.geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    this.geometry.setAttribute("color1", new THREE.BufferAttribute(colors1, 3));
    this.geometry.setAttribute("color2", new THREE.BufferAttribute(colors2, 3));

    this.mesh = new THREE.Mesh(this.geometry, this.colorBufferMaterial);
    this.add(this.mesh);
  }

  setColorBufferMaterial() {
    this.mesh.material = this.colorBufferMaterial;
  }

  setUniformColorMaterial() {
    this.mesh.material = this.uniformColorMaterial;
  }

  setUniformColor(color) {
    this.uniformColorMaterial.color.setStyle(color);
  }

  dispose() {
    this.geometry.dispose();
    this.colorBufferMaterial.dispose();
    this.uniformColorMaterial.dispose();
  }
}

class ScanTissue extends THREE.Object3D {
  constructor(scanData, tissueFaceIndices) {
    super();

    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.MeshStandardMaterial({
      color: Colors.gray,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.4,
      transparent: true,
      opacity: 0.5,
    });

    const positions = new Float32Array(tissueFaceIndices.length * 3 * 3);
    const normals = new Float32Array(positions.length);

    let currentPositionIndex = 0;
    for (const faceIndex of tissueFaceIndices) {
      const index = faceIndex * 3;
      const vertexIndices = scanData.indices.slice(index, index + 3);
      for (const vertexIndex of vertexIndices) {
        const positionIndex = vertexIndex * 3;
        positions.set(
          scanData.positions.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        normals.set(
          scanData.normals.slice(positionIndex, positionIndex + 3),
          currentPositionIndex
        );
        currentPositionIndex += 3;
      }
    }

    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    this.geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.add(this.mesh);
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
  }
}

function setScoreColor(color, score) {
  if (score < THRESHOLDS.CONSIDER_RESCAN) {
      return color.setStyle("#FF5252");
    }
  if (score < THRESHOLDS.PASS) {
    return color.setStyle("#FFC107");
  }
  return color.setStyle("#4CAF50");
}
